import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuthStore } from '../../store/authStore';
import { useTeamId } from '../../hooks/useTeamId';
import { useTeamRole } from '../../hooks/useTeamRole';
import { footerItems } from './navConfig';

export default function SidebarFooter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { teamId } = useTeamId();
  const { role, isLoading: roleLoading } = useTeamRole(teamId);

  // Filter footer items by the user's team role. Falls open during initial
  // load (no role yet) so OWNER doesn't see a flicker, then re-renders once
  // the role resolves. If role data fails entirely (Free plan / network),
  // fall back to showing items only when no requiredRoles is set — never
  // expose admin items to a user whose role we don't know.
  const visibleFooterItems = footerItems.filter((item) => {
    if (!item.requiredRoles) return true;
    if (roleLoading) return false; // hide while loading rather than flash
    if (!role) return false;
    return item.requiredRoles.includes(role);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isPathActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <div className="shrink-0 border-t border-rule pt-4 pb-4">
      <nav className="space-y-0.5 px-3">
        {visibleFooterItems.map((item) => {
          const isActive = isPathActive(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={clsx(
                'group flex items-center gap-x-3 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-teal-50 text-ink'
                  : 'text-ink-3 hover:bg-panel-2 hover:text-ink'
              )}
            >
              <item.icon
                className={clsx(
                  'h-4 w-4 shrink-0',
                  isActive ? 'text-teal-600' : 'text-ink-4 group-hover:text-ink-2'
                )}
              />
              <span className="truncate">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      {user && (
        <div className="mt-4 mx-3 rounded-md border border-rule bg-panel-2 px-3 py-2.5 space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-semibold text-teal-700">
              {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">
                {user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.email}
              </p>
              <p className="truncate text-xs text-ink-3">{user.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-rule bg-panel px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-panel-2 hover:text-ink transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
